/**
 * Shared UI constants for consistent sizing across components
 */

/**
 * SignIn card dimensions
 * Used to keep the blur overlay in RootLayout synchronized with the actual card in SignIn
 */
export const SIGNIN_CARD = {
  /** Maximum width of the signin card */
  maxWidth: "max-w-sm", // Tailwind class: 24rem / 384px

  /** Approximate height of the signin card content */
  minHeight: 355, // pixels - matches actual rendered card height
} as const;

/**
 * Layout dimensions used across pages for consistent spacing and animations
 * Heights are optimized for different device categories based on viewport size
 */
export const LAYOUT = {
  /** Minimum viewport height before TitleHero fades out (to prioritize tab content) */
  MIN_VIEWPORT_HEIGHT_FOR_HERO: 1023,

  /** Height of the main navigation header */
  HEADER_HEIGHT: {
    /** Mobile (< 640px) - Compact header */
    MOBILE: 100,
    /** Tablet (640px - 1023px) - Standard header */
    TABLET: 72,
    /** Desktop (≥ 1024px) - Full-size header */
    DESKTOP: 80,
  },

  /** Height of collapsed tab section on title detail pages */
  TAB_COLLAPSED_HEIGHT: {
    /** Mobile (< 640px) - Compact tabs */
    MOBILE: 70,
    /** Tablet (640px - 1023px) - Standard tabs */
    TABLET: 110,
    /** Desktop (≥ 1024px) - Full-size tabs */
    DESKTOP: 0,
  },

  /**
   * Distance to slide the basket up when tabs expand (translateY amount)
   * Desktop: Controls how far up the basket moves (smaller = less movement)
   */
  TAB_SLIDE_DISTANCE: {
    /** Mobile (< 640px) - How far to slide up */
    MOBILE: 0,
    /** Tablet (640px - 1023px) - How far to slide up */
    TABLET: 0,
    /** Desktop (≥ 1024px) - How far to slide up */
    DESKTOP: 0,
  },

  /**
   * Height of the tab content container when expanded
   * Desktop: Controls how tall the visible content area is (larger = more content visible)
   */
  TAB_CONTENT_HEIGHT: {
    /** Mobile (< 640px) - Full viewport minus header and tab bar */
    MOBILE: "calc(100vh - 72px - 100px)",
    /** Tablet portrait (640px - 1023px) - Full viewport minus header and tab bar */
    TABLET: 400,
    /** Desktop (≥ 1024px) - Fixed height for content scrolling */
    DESKTOP: 400,
  },
} as const;

/**
 * Responsive breakpoints matching Tailwind CSS defaults
 * Use these constants for JavaScript-based responsive logic
 *
 * @see /docs/guides/responsive-design-strategy.md for complete strategy
 * @see /docs/guides/breakpoint-quick-reference.md for quick reference
 */
export const BREAKPOINTS = {
  /** Mobile breakpoint (Tailwind sm:) - Mobile landscape, small tablets */
  SMALL: 640,

  /** Tablet breakpoint (Tailwind md:) - iPad portrait, tablet devices */
  MEDIUM: 768,

  /** Desktop breakpoint (Tailwind lg:) - PRIMARY TRANSITION POINT
   * Below 1024px = Touch-optimized (mobile/tablet portrait)
   * At 1024px+ = Mouse-optimized (desktop/tablet landscape)
   */
  LARGE: 1024,

  /** Large desktop breakpoint (Tailwind xl:) */
  EXTRA_LARGE: 1280,

  /** Extra large desktop breakpoint (Tailwind 2xl:) */
  EXTRA_EXTRA_LARGE: 1536,
} as const;

/**
 * Height breakpoints for vertical space considerations
 * Critical for differentiating portrait vs landscape at same width
 *
 * Example: 1024px width can be:
 * - 1024×1366 portrait (TALL) = lots of vertical space
 * - 1366×1024 landscape (SHORT) = limited vertical space
 */
export const HEIGHT_BREAKPOINTS = {
  /** Short viewports (landscape phones, cramped laptops) */
  SHORT: 700,

  /** Medium height (landscape tablets, standard laptops) */
  MEDIUM: 900,

  /** Tall viewports (portrait tablets, desktop monitors) */
  TALL: 1200,
} as const;

/**
 * Viewport dimensions helper
 * Get current viewport size in a testable way
 */
export const VIEWPORT = {
  /** Get current viewport width (default 1024 for SSR) */
  getWidth: (): number => globalThis?.window?.innerWidth ?? 1024,

  /** Get current viewport height (default 768 for SSR) */
  getHeight: (): number => globalThis?.window?.innerHeight ?? 768,

  /** Get current viewport dimensions */
  getDimensions: (): { width: number; height: number } => ({
    width: globalThis?.window?.innerWidth ?? 1024,
    height: globalThis?.window?.innerHeight ?? 768,
  }),

  /** Check if viewport is portrait (height > width) */
  isPortrait: (): boolean => {
    const { width, height } = VIEWPORT.getDimensions();
    return height > width;
  },

  /** Check if viewport is landscape (width > height) */
  isLandscape: (): boolean => {
    const { width, height } = VIEWPORT.getDimensions();
    return width > height;
  },

  /** Get aspect ratio (width / height) */
  getAspectRatio: (): number => {
    const { width, height } = VIEWPORT.getDimensions();
    return width / height;
  },
} as const;

/**
 * Device category detection based on BOTH width and height
 * This is the CANONICAL way to categorize devices in Lumi
 *
 * Replaces ad-hoc viewport checks throughout the codebase
 *
 * @example
 * const { width, height } = VIEWPORT.getDimensions();
 * if (DEVICE.isPortraitTablet(width, height)) {
 *   // Show hero, lots of vertical space
 * }
 */
export const DEVICE = {
  /** Phone portrait: < 640px width */
  isPhonePortrait: (width: number, _height: number): boolean => width < BREAKPOINTS.SMALL,

  /** Phone landscape: < 1024px width, aspect ratio > 1.5 (phones ~2.0+, tablets ~1.3) */
  isPhoneLandscape: (width: number, height: number): boolean => width < BREAKPOINTS.LARGE && width / height > 1.5,

  /** Tablet portrait: 640-1536px width, taller than wide */
  isTabletPortrait: (width: number, height: number): boolean =>
    width >= BREAKPOINTS.SMALL && width < BREAKPOINTS.EXTRA_EXTRA_LARGE && height > width,

  /** Tablet landscape: 1024-1536px width, wider than tall, height < 900px */
  isTabletLandscape: (width: number, height: number): boolean =>
    width >= BREAKPOINTS.LARGE &&
    width < BREAKPOINTS.EXTRA_EXTRA_LARGE &&
    height < width &&
    height < HEIGHT_BREAKPOINTS.MEDIUM,

  /** Desktop: ≥1536px width OR (≥1024px width AND ≥900px height) */
  isDesktop: (width: number, height: number): boolean =>
    width >= BREAKPOINTS.EXTRA_EXTRA_LARGE || (width >= BREAKPOINTS.LARGE && height >= HEIGHT_BREAKPOINTS.MEDIUM),

  /** Check if device is touch-optimized (phones + portrait tablets) */
  isTouchOptimized: (width: number, height: number): boolean =>
    width < BREAKPOINTS.LARGE || (width < BREAKPOINTS.EXTRA_EXTRA_LARGE && height > width),

  /** Check if device is mouse-optimized (desktop + landscape tablets) */
  isMouseOptimized: (width: number, height: number): boolean => !DEVICE.isTouchOptimized(width, height),

  /**
   * Get device category name for debugging/logging
   * Returns: 'phone-portrait' | 'phone-landscape' | 'tablet-portrait' | 'tablet-landscape' | 'desktop'
   */
  getCategory: (width: number, height: number): string => {
    if (DEVICE.isPhonePortrait(width, height)) return "phone-portrait";
    if (DEVICE.isPhoneLandscape(width, height)) return "phone-landscape";
    if (DEVICE.isTabletPortrait(width, height)) return "tablet-portrait";
    if (DEVICE.isTabletLandscape(width, height)) return "tablet-landscape";
    if (DEVICE.isDesktop(width, height)) return "desktop";
    return "unknown";
  },
} as const;

/**
 * Responsive layout helpers to get appropriate dimensions for current viewport
 *
 * @example
 * const headerHeight = RESPONSIVE_LAYOUT.getHeaderHeight(window.innerWidth);
 * const tabHeight = RESPONSIVE_LAYOUT.getTabCollapsedHeight(window.innerWidth);
 */
export const RESPONSIVE_LAYOUT = {
  /** Get header height for current viewport width */
  getHeaderHeight: (width: number): number => {
    if (width < BREAKPOINTS.SMALL) return LAYOUT.HEADER_HEIGHT.MOBILE;
    if (width < BREAKPOINTS.LARGE) return LAYOUT.HEADER_HEIGHT.TABLET;
    return LAYOUT.HEADER_HEIGHT.DESKTOP;
  },

  /** Get collapsed tab height for current viewport width */
  getTabCollapsedHeight: (width: number): number => {
    if (width < BREAKPOINTS.SMALL) return LAYOUT.TAB_COLLAPSED_HEIGHT.MOBILE;
    if (width < BREAKPOINTS.LARGE) return LAYOUT.TAB_COLLAPSED_HEIGHT.TABLET;
    return LAYOUT.TAB_COLLAPSED_HEIGHT.DESKTOP;
  },

  /** Get expanded tab content height for current viewport width */
  getTabContentHeight: (width: number): string | number => {
    if (width < BREAKPOINTS.SMALL) return LAYOUT.TAB_CONTENT_HEIGHT.MOBILE;
    if (width < BREAKPOINTS.LARGE) return LAYOUT.TAB_CONTENT_HEIGHT.TABLET;
    return LAYOUT.TAB_CONTENT_HEIGHT.DESKTOP;
  },

  /** Get tab slide distance for current viewport width */
  getTabSlideDistance: (width: number): number => {
    if (width < BREAKPOINTS.SMALL) return LAYOUT.TAB_SLIDE_DISTANCE.MOBILE;
    if (width < BREAKPOINTS.LARGE) return LAYOUT.TAB_SLIDE_DISTANCE.TABLET;
    return LAYOUT.TAB_SLIDE_DISTANCE.DESKTOP;
  },
} as const;
